/**
 * Voyager Pro Theme Constants (formerly "Travelie")
 *
 * Palette/fonts extracted from Figma Make: figma.com/make/oFpSdzeo1f51AfW7XfNZYn
 * "Travel Quote Builder Design" (2026-08-03 CRM-wide reskin), superseding the
 * original 2026-06-03 Travelie palette this file was first extracted from
 * (figma.com/design/UWo2M9LG07KktPRdlbXuFL/).
 *
 * These values are mirrored as CSS custom properties in
 * src/styles/voyagerTheme.css for components that use plain CSS/Tailwind
 * classes instead of this JS theme object - keep both in sync.
 *
 * This file contains the complete design system for the Voyager Pro theme.
 * Use these constants for consistent styling across the agent dashboard.
 */

// ============================================================================
// COLOR PALETTE
// ============================================================================

export const colors = {
  // Primary Colors (gold accent - matches --accent; used for primary CTAs,
  // links, and active states across existing components)
  primary: {
    main: '#C4871F',        // Accent gold - buttons, links, active states
    light: '#FBF0DF',       // Light gold tint - backgrounds, hover states
    hover: '#A8710C',       // Darker gold for hover
  },

  // Navy (matches --primary/--sidebar) - dark surfaces: sidebar, headers,
  // hero/banner backgrounds
  navy: {
    main: '#1A2B4A',
    hover: '#243A5E',
  },

  // Status Colors
  status: {
    success: '#10B981',     // Green - confirmed, success states
    successLight: '#D1FAE5', // Light green background
    warning: '#F59E0B',     // Orange/Yellow - pending, warning states
    error: '#EF4444',       // Red - error, cancelled states
    info: '#3B9EFF',        // Blue - information badges
  },

  // Neutral Colors
  neutral: {
    background: '#F5F2EC',  // Main page background (cream)
    card: '#FFFFFF',        // White cards
    border: 'rgba(26, 43, 74, 0.1)', // Subtle borders
    hover: '#F0EDE5',       // Hover state for rows/cards
  },

  // Text Colors
  text: {
    primary: '#1A2B4A',     // Headings, main text (navy)
    secondary: '#6B7A94',   // Labels, secondary text
    muted: '#94A3B8',       // Placeholder, disabled text
    white: '#FFFFFF',       // White text for dark backgrounds
  },

  // Chart Colors (for future use)
  chart: {
    blue: '#3B9EFF',
    lightBlue: '#93C5FD',
    segment1: '#3B9EFF',
    segment2: '#60A5FA',
    segment3: '#93C5FD',
    segment4: '#BFDBFE',
  },

  // Stat Card Icon Backgrounds (matching Travelie)
  statIcons: {
    blue: '#3B9EFF',        // Clients/bookings
    green: '#10B981',       // Revenue/earnings
    purple: '#8B5CF6',      // Calendar/itineraries
    orange: '#F59E0B',      // Pending/warnings
  },
};

// ============================================================================
// TYPOGRAPHY
// ============================================================================

export const typography = {
  // Font Family
  fontFamily: {
    primary: '"Outfit", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: '"DM Serif Display", Georgia, serif', // Headings/hero numbers
    mono: '"DM Mono", monospace',                  // Prices/quote numbers
    fallback: 'system-ui, sans-serif',
  },

  // Font Sizes
  fontSize: {
    xs: '12px',             // Small labels, badges
    sm: '14px',             // Body text, table cells
    base: '16px',           // Default body
    lg: '18px',             // Section headings
    xl: '20px',             // Card titles
    '2xl': '24px',          // Page titles
    '3xl': '28px',          // Large metrics
    '4xl': '32px',          // Hero numbers
  },

  // Font Weights
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  // Line Heights
  lineHeight: {
    tight: 1.2,             // Headings
    normal: 1.5,            // Body text
    relaxed: 1.6,           // Comfortable reading
  },
};

// ============================================================================
// SPACING SCALE
// ============================================================================

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
  '3xl': '32px',
  '4xl': '40px',
  '5xl': '48px',
};

// ============================================================================
// RESPONSIVE BREAKPOINTS
// ============================================================================

export const breakpoints = {
  mobile: '480px',      // Small phones
  tablet: '768px',      // Tablets and large phones
  desktop: '1024px',    // Desktop and laptops
  wide: '1440px',       // Large desktop screens
};

// Media query helpers for CSS-in-JS
export const mediaQueries = {
  mobile: `@media (max-width: ${breakpoints.tablet})`,
  tablet: `@media (min-width: ${breakpoints.tablet}) and (max-width: ${breakpoints.desktop})`,
  desktop: `@media (min-width: ${breakpoints.desktop})`,
  wide: `@media (min-width: ${breakpoints.wide})`,
  
  // Utility queries
  mobileOnly: `@media (max-width: calc(${breakpoints.tablet} - 1px))`,
  tabletUp: `@media (min-width: ${breakpoints.tablet})`,
  desktopUp: `@media (min-width: ${breakpoints.desktop})`,
};

// ============================================================================
// COMPONENT STYLES
// ============================================================================

export const components = {
  // Stat Cards
  statCard: {
    background: colors.neutral.card,
    borderRadius: '12px',
    padding: spacing['2xl'],
    shadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    gap: spacing.lg,
    iconSize: '24px',
    iconPadding: '12px',
    iconBorderRadius: '8px',
  },

  // Buttons
  button: {
    primary: {
      background: colors.primary.main,
      color: colors.text.white,
      hover: colors.primary.hover,
      borderRadius: '8px',
      padding: `${spacing.md} ${spacing.xl}`,
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      height: '40px',
    },
    secondary: {
      background: colors.primary.light,
      color: colors.primary.main,
      hover: '#D1E9FF',
      borderRadius: '8px',
      padding: `${spacing.md} ${spacing.xl}`,
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      height: '40px',
    },
  },

  // Status Badges
  badge: {
    borderRadius: '16px',        // Pill shape
    padding: `4px ${spacing.md}`,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium,
    variants: {
      draft: {
        background: '#F1F5F9',
        color: '#475569',
      },
      sent: {
        background: '#DBEAFE',
        color: '#1E40AF',
      },
      accepted: {
        background: '#D1FAE5',
        color: '#065F46',
      },
      pending: {
        background: '#FEF3C7',
        color: '#92400E',
      },
      error: {
        background: '#FEE2E2',
        color: '#991B1B',
      },
    },
  },

  // Tables/Lists
  table: {
    headerBackground: '#F8FAFC',
    rowHover: colors.neutral.hover,
    headerPadding: `${spacing.md} ${spacing.lg}`,
    cellPadding: `${spacing.md} ${spacing.lg}`,
    border: `1px solid ${colors.neutral.border}`,
    borderRadius: '12px',
  },

  // Input Fields
  input: {
    borderRadius: '8px',
    padding: `${spacing.sm} ${spacing.md}`,
    border: `1px solid ${colors.neutral.border}`,
    fontSize: typography.fontSize.sm,
  },

  // Cards (for lists)
  card: {
    background: colors.neutral.card,
    borderRadius: '8px',
    padding: spacing.lg,
    shadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    hover: {
      shadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-2px)',
    },
  },
};

// ============================================================================
// LAYOUT
// ============================================================================

export const layout = {
  // Container
  container: {
    maxWidth: '1400px',
    padding: spacing['3xl'],
  },

  // Grid
  grid: {
    gap: spacing['2xl'],
    columns: {
      mobile: 1,
      tablet: 2,
      desktop: 4,
    },
  },

  // Sidebar (for reference)
  sidebar: {
    width: '240px',
    background: colors.neutral.card,
    padding: spacing.lg,
  },
};

// ============================================================================
// TRANSITIONS
// ============================================================================

export const transitions = {
  fast: '150ms ease-in-out',
  normal: '200ms ease-in-out',
  slow: '300ms ease-in-out',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get status badge styles based on status value
 */
export const getStatusBadgeStyle = (status) => {
  const statusLower = status?.toLowerCase() || 'draft';
  
  const statusMap = {
    // Quote statuses
    draft: components.badge.variants.draft,
    sent: components.badge.variants.sent,
    accepted: components.badge.variants.accepted,
    rejected: components.badge.variants.error,
    cancelled: components.badge.variants.error,
    
    // Commission statuses
    approved: { background: '#D1FAE5', color: '#059669' },
    paid: { background: '#DCFCE7', color: '#16A34A', fontWeight: 600 },
    pending: { background: '#FEF3C7', color: '#F59E0B' },
    denied: { background: '#FEE2E2', color: '#DC2626' },
    on_hold: { background: '#E9D5FF', color: '#8B5CF6' },
    needs_reply: { background: '#DBEAFE', color: '#1E40AF', animation: 'pulse 2s infinite' },
    needs_agent_reply: { background: '#DBEAFE', color: '#1E40AF', animation: 'pulse 2s infinite' },
    needs_admin_reply: { background: '#FEF3C7', color: '#F59E0B', animation: 'pulse 2s infinite' },
    processing: { background: '#FEF3C7', color: '#F59E0B' },
  };

  return statusMap[statusLower] || components.badge.variants.draft;
};

/**
 * Get stat card icon background color
 */
export const getStatIconColor = (type) => {
  const iconMap = {
    clients: colors.statIcons.blue,
    quotes: colors.statIcons.green,
    itineraries: colors.statIcons.purple,
    commissions: colors.statIcons.orange,
  };

  return iconMap[type] || colors.statIcons.blue;
};

/**
 * Format currency
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
};

/**
 * Format date
 */
export const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

export default {
  colors,
  typography,
  spacing,
  breakpoints,
  mediaQueries,
  components,
  layout,
  transitions,
  getStatusBadgeStyle,
  getStatIconColor,
  formatCurrency,
  formatDate,
};
